<template>
  <div class="file-explorer-page">
    <div class="explorer-container">
      <file-explorer-component
        title="SECURAMDATA - Drive"
        @item-selected="handleItemSelected"
        @file-open="handleFileOpen"
        @delete-item="deleteItemFromContext"
        @download-file="downloadFile"
        @download-directory="handleDirectoryDownload"
        @show-upload-dialog="showUploadDialog = true"
        @show-new-folder-dialog="
          showNewFolderDialog = true
          focusNewFolderInput()
        "
        @show-item-details="handleShowItemDetails"
        @item-renamed="handleItemRenamed"
        @share-request="handleShareRequest"
        @show-sharing-info="handleShowSharingInfo"
        ref="fileExplorer"
      />
    </div>

    <!-- Modal para upload de arquivos -->
    <vs-popup
      title="Upload de Arquivo"
      :active.sync="showUploadDialog"
      class="file-explorer-popup"
    >
      <div class="upload-dialog-content">
        <div
          class="upload-area"
          @click="triggerFileInput"
          @dragover.prevent
          @drop.prevent="handleFileDrop"
        >
          <feather-icon icon="UploadCloudIcon" size="40" />
          <p>Arraste arquivos aqui ou clique para selecionar</p>
          <input
            type="file"
            ref="fileInput"
            style="display: none"
            @change="handleFileInputChange"
            multiple
          />
        </div>
        <div v-if="selectedFiles.length > 0" class="selected-files-list">
          <div
            v-for="(file, index) in selectedFiles"
            :key="index"
            class="selected-file"
          >
            <span>{{ file.name }}</span>
            <b-button
              variant="outline-danger"
              size="sm"
              @click="removeFile(index)"
            >
              <feather-icon icon="XIcon" size="16" />
            </b-button>
          </div>
          <div class="selected-files-count">
            {{ selectedFiles.length }} arquivo(s) selecionado(s)
          </div>
        </div>
      </div>
      <div class="footer-dialog">
        <b-button
          variant="primary"
          @click="uploadFiles"
          :disabled="!selectedFiles.length"
          :class="{ 'b-button-loading': uploading }"
        >
          <feather-icon icon="UploadIcon" size="16" class="mr-1" />
          Upload
        </b-button>
        <b-button
          variant="outline-secondary"
          @click="showUploadDialog = false"
          class="ml-2"
        >
          <feather-icon icon="XIcon" size="16" class="mr-1" />
          Cancelar
        </b-button>
      </div>
    </vs-popup>

    <!-- Modal para criar nova pasta -->
    <vs-popup
      title="Nova Pasta"
      :active.sync="showNewFolderDialog"
      class="file-explorer-popup"
    >
      <div class="new-folder-content">
        <div class="mb-3">
          <label for="folderName">Nome da Pasta</label>
          <b-form-input
            id="folderName"
            ref="newFolderNameInput"
            v-model="newFolderName"
            placeholder="Digite o nome da pasta"
            class="w-100"
            @keyup.enter="createFolder"
          />
        </div>
      </div>
      <div class="footer-dialog">
        <b-button
          variant="primary"
          @click="createFolder"
          :disabled="!newFolderName"
          :class="{ 'b-button-loading': creatingFolder }"
        >
          <feather-icon icon="FolderPlusIcon" size="16" class="mr-1" />
          Criar
        </b-button>
        <b-button
          variant="outline-secondary"
          @click="showNewFolderDialog = false"
          class="ml-2"
        >
          <feather-icon icon="XIcon" size="16" class="mr-1" />
          Cancelar
        </b-button>
      </div>
    </vs-popup>

    <!-- Modal para visualizar detalhes do arquivo -->
    <file-details-popup v-model="showItemDetails" :item="selectedItem">
      <template #footer-actions>
        <b-button
          v-if="
            selectedItem &&
              selectedItem.type === 'file' &&
              selectedItem.is_owner
          "
          v-permission="'drive.delete_file'"
          variant="danger"
          @click="confirmDeleteItemInPopup"
          :class="{ 'b-button-loading': deleting }"
        >
          <feather-icon icon="Trash2Icon" size="16" class="mr-1" />
          Excluir
        </b-button>
        <b-button
          v-if="
            selectedItem &&
              selectedItem.type === 'directory' &&
              selectedItem.is_owner
          "
          v-permission="'drive.delete_directory'"
          variant="danger"
          @click="confirmDeleteItemInPopup"
          :class="{ 'b-button-loading': deleting }"
        >
          <feather-icon icon="Trash2Icon" size="16" class="mr-1" />
          Excluir
        </b-button>
      </template>
    </file-details-popup>

    <!-- Modal para compartilhamento de recurso -->
    <share-resource-popup
      v-model="showShareResourcePopup"
      :resource="shareResource"
      :fileExplorerService="fileExplorerService"
      @shared="handleResourceShared"
    />

    <sharing-details-popup
      v-model="showSharingDetails"
      :item="sharingDetailsItem"
    />
  </div>
</template>

<script>
import FileExplorerComponent from '@/components/FileExplorer/FileExplorerComponent.vue'
import DriveService from '@/services/api/DriveService'
import ShareResourcePopup from '@/components/FileExplorer/ShareResourcePopup.vue'
import FileDetailsPopup from '@/components/FileExplorer/FileDetailsPopup.vue'
import SharingDetailsPopup from '@/components/FileExplorer/SharingDetailsPopup.vue'
import { BButton, BFormInput } from 'bootstrap-vue'
import '@/assets/scss/FileExplorer.scss'

export default {
  name: 'FileExplorer',
  components: {
    FileExplorerComponent,
    BButton,
    BFormInput,
    ShareResourcePopup,
    FileDetailsPopup,
    SharingDetailsPopup
  },
  data() {
    return {
      // Upload de arquivos
      showUploadDialog: false,
      selectedFiles: [],
      uploading: false,

      // Nova pasta
      showNewFolderDialog: false,
      newFolderName: '',
      creatingFolder: false,

      // Detalhes do item
      showItemDetails: false,
      selectedItem: null,
      deleting: false,

      // Compartilhamento
      showShareResourcePopup: false,
      shareResource: null,

      // Detalhes de compartilhamento
      showSharingDetails: false,
      sharingDetailsItem: null,

      // Estado atual
      currentPath: '',

      // Instância do serviço
      fileExplorerService: null
    }
  },
  created() {
    // Criar instância do serviço
    this.fileExplorerService = DriveService.build(this.$vs)
  },
  methods: {
    // Métodos de gerenciamento de arquivos
    async refreshExplorer() {
      if (this.$refs.fileExplorer) {
        this.$refs.fileExplorer.refresh()
      }
    },

    // Eventos do explorador de arquivos
    handleItemSelected(item) {
      this.selectedItem = item

      // Atualizar o path atual
      if (item.path) {
        this.currentPath = item.path
      }

      // Apenas mostrar detalhes se for um arquivo (não uma pasta)
      if (item.type !== 'directory' && item.type !== 'drive') {
        this.showItemDetails = true
      }
      // Para pastas e drives, apenas seleciona sem mostrar o popup
    },

    handleFileOpen(file) {
      if (file.type === 'file') {
        // Se o arquivo tiver url_tmp, use-a para download
        if (file.url_tmp) {
          this.downloadFile(file)
        } else {
          this.notifyError(
            this.$vs,
            'URL temporária do recurso não encontrada para arquivo.'
          )
          // Fallback para o método antigo, caso a url_tmp não esteja disponível
          //window.open(this.fileExplorerService.getFileDownloadUrl(file.path), '_blank');
        }
      }
    },

    handleDirectoryDownload(directory) {
      if (directory.type === 'directory' || directory.type === 'drive') {
        try {
          // Verificar se temos um resource_id válido
          if (!directory.resource_id) {
            this.notifyError(
              this.$vs,
              'ID do recurso não encontrado para este drive/pasta.'
            )
            return
          }
          this.notifySuccess(
            this.$vs,
            'Aguarde... processando sua solicitação!'
          )

          // Chamar o método de download passando o resource_id em vez do path
          this.fileExplorerService.downloadDirectoryAsZip(
            directory.resource_id,
            directory.name,
            directory.type === 'directory' ? directory.name : 'drive'
          )

          this.notifySuccess(this.$vs, 'O download foi iniciado.')
        } catch (error) {
          console.error('Erro ao baixar pasta:', error)
          this.notifyError(
            this.$vs,
            `Falha ao baixar ${
              directory.type === 'directory' ? 'a pasta' : 'o drive.'
            }`
          )
        }
      }
    },
    confirmDeleteItemInPopup() {
      const itemType =
        this.selectedItem.type === 'directory' ? 'pasta' : 'arquivo'
      const message = `Tem certeza que deseja excluir ${
        itemType === 'pasta' ? 'a' : 'o'
      } ${itemType} "${
        this.selectedItem.name
      }"? Esta ação não pode ser desfeita, e, essa referência será removida das outras funções do sistema.`

      // Exibir diálogo de confirmação
      this.$vs.dialog({
        type: 'confirm',
        color: 'warning',
        title: 'Atenção!',
        acceptText: 'Sim',
        cancelText: 'Não',
        text: message,
        accept: () => {
          console.log('Delete item:', {
            item: this.selectedItem,
            parent_id: this.selectedItem.parent_id,
            resource_id: this.selectedItem.resource_id,
            type: this.selectedItem.type,
            name: this.selectedItem.name
          })
          this.deleteItem()
        }
      })
    },

    // Exclusão de item, from context menu
    deleteItemFromContext(item) {
      this.selectedItem = item

      // Verificar se o item tem as propriedades necessárias para exclusão
      if (item.type === 'file' && !item.media_id) {
        this.notifyError(
          this.$vs,
          'Erro ao excluir arquivo: Media ID não encontrado.',
          3000
        )
        return
      }

      this.deleteItem()
        .then(() => {
          // Após excluir com sucesso, atualizamos o explorador
          this.refreshExplorer()

          // Se for um diretório, notificar o componente FileExplorerComponent
          if (item.type === 'directory' && this.$refs.fileExplorer) {
            console.log(
              'Emitindo evento de pasta excluída com parent_id:',
              item.parent_id
            )

            // Garantir que o parent_id esteja incluído no item passado para o evento
            this.$refs.fileExplorer.$emit('item-deleted', {
              type: item.type,
              name: item.name,
              resource_id: item.resource_id,
              parent_id: item.parent_id
            })
          }
        })
        .catch(error => {
          console.error('Erro na exclusão:', error)
        })
    },

    // Upload de arquivos
    triggerFileInput() {
      this.$refs.fileInput.click()
    },

    handleFileInputChange(event) {
      if (event.target.files.length > 0) {
        // Converte FileList para Array e adiciona aos arquivos selecionados
        const newFiles = Array.from(event.target.files)
        this.selectedFiles = [...this.selectedFiles, ...newFiles]
      }
      // Limpar o input para permitir selecionar os mesmos arquivos novamente
      event.target.value = ''
    },

    handleFileDrop(event) {
      const files = event.dataTransfer.files
      if (files.length > 0) {
        // Converte FileList para Array e adiciona aos arquivos selecionados
        const newFiles = Array.from(files)
        this.selectedFiles = [...this.selectedFiles, ...newFiles]
      }
    },

    removeFile(index) {
      this.selectedFiles.splice(index, 1)
    },

    async uploadFiles() {
      if (!this.selectedFiles.length) return

      this.uploading = true
      try {
        // Obter o resource_id da pasta atual diretamente do explorador
        const currentResourceId = this.$refs.fileExplorer
          ? this.$refs.fileExplorer.currentResourceId
          : undefined

        // Enviar todos os arquivos selecionados de uma vez
        const response = await this.fileExplorerService.uploadFile(
          this.currentPath,
          this.selectedFiles,
          currentResourceId
        )

        this.notifySuccess(
          this.$vs,
          this.selectedFiles.length > 1
            ? 'Arquivos carregados com sucesso'
            : 'Arquivo carregado com sucesso'
        )

        // Criar objetos representando os arquivos enviados (para atualização da árvore)
        if (
          this.$refs.fileExplorer &&
          typeof this.$refs.fileExplorer.updateTreeAfterOperation === 'function'
        ) {
          // Se temos apenas um arquivo ou a API retornou informações sobre os arquivos
          if (this.selectedFiles.length === 1 || (response && response.files)) {
            const files =
              response && response.files
                ? response.files
                : [
                    {
                      name: this.selectedFiles[0].name,
                      type: 'file',
                      parent_id: currentResourceId,
                      // Se a API retornar IDs para os arquivos, podemos usá-los
                      resource_id:
                        response && response.resource_id
                          ? response.resource_id
                          : undefined,
                      media_id:
                        response && response.media_id
                          ? response.media_id
                          : undefined
                    }
                  ]

            // Para cada arquivo, atualizar a árvore
            files.forEach(file => {
              this.$refs.fileExplorer.updateTreeAfterOperation(file, 'create')
            })
          } else {
            // Se temos múltiplos arquivos e não temos detalhes individuais,
            // criar um objeto genérico representando um arquivo na pasta atual
            const genericFile = {
              name: 'uploaded_file', // Nome genérico
              type: 'file',
              parent_id: currentResourceId
            }

            // Atualizar a árvore uma vez para todos os arquivos
            this.$refs.fileExplorer.updateTreeAfterOperation(
              genericFile,
              'create'
            )
          }
        } else {
          // Fallback se o método não estiver disponível
          this.refreshExplorer()
        }

        this.selectedFiles = []
        this.showUploadDialog = false
      } catch (error) {
        this.notifyError(
          this.$vs,
          'Falha ao carregar os arquivos. Por favor, tente novamente.',
          3000
        )
      } finally {
        this.uploading = false
      }
    },

    // Gerenciamento de pastas
    async createFolder() {
      if (!this.newFolderName) return

      this.creatingFolder = true
      try {
        // Obter o resource_id da pasta atual diretamente do explorador
        const currentResourceId = this.$refs.fileExplorer
          ? this.$refs.fileExplorer.currentResourceId
          : undefined

        // Passar o resource_id junto com o path e nome
        const response = await this.fileExplorerService.createDirectory(
          this.currentPath,
          this.newFolderName,
          currentResourceId
        )

        this.notifySuccess(this.$vs, this.$t('messages.success'))
        this.newFolderName = ''
        this.showNewFolderDialog = false

        // Construir um objeto representando a nova pasta
        const newFolder = {
          name: this.newFolderName,
          path: this.currentPath
            ? `${this.currentPath}/${this.newFolderName}`
            : this.newFolderName,
          type: 'directory',
          parent_id: currentResourceId,
          resource_id:
            response && response.resource_id ? response.resource_id : undefined,
          folder_count: 0,
          file_count: 0
        }

        // Atualizar a árvore usando o método genérico
        if (
          this.$refs.fileExplorer &&
          typeof this.$refs.fileExplorer.updateTreeAfterOperation === 'function'
        ) {
          this.$refs.fileExplorer.updateTreeAfterOperation(newFolder, 'create')
        } else {
          // Fallback se o método não estiver disponível
          this.refreshExplorer()
        }
      } catch (error) {
        console.error('Erro ao criar pasta:', error)
        this.notifyError(
          this.$vs,
          'Não foi possível criar a pasta. Por favor, tente novamente.',
          3000
        )
      } finally {
        this.creatingFolder = false
      }
    },

    // Gerenciamento de itens
    async deleteItem() {
      if (!this.selectedItem)
        return Promise.reject(new Error('Nenhum item selecionado'))

      this.deleting = true
      try {
        // Extrair os dados necessários do item selecionado
        const { path, resource_id, type } = this.selectedItem

        // Armazenar uma cópia do item que será excluído
        const itemToDelete = { ...this.selectedItem }

        console.log('Tentando excluir item:', {
          type,
          resource_id,
          media_id: this.selectedItem.media_id,
          parent_id: this.selectedItem.parent_id,
          item: itemToDelete
        })

        // Para arquivos, usar o media_id para exclusão
        if (type === 'file') {
          if (!this.selectedItem.media_id) {
            throw new Error(
              `Media ID não encontrado para o arquivo: ${this.selectedItem.name}`
            )
          }

          await this.fileExplorerService.deleteItem(
            path,
            resource_id,
            type,
            this.selectedItem.media_id // Passar o media_id para arquivos
          )
        } else {
          // Para pastas, usar apenas resource_id
          await this.fileExplorerService.deleteItem(path, resource_id, type)
        }

        this.notifySuccess(
          this.$vs,
          `${type === 'directory' ? 'Pasta' : 'Arquivo'} excluído com sucesso`
        )
        this.showItemDetails = false

        // Atualizar a árvore usando o método genérico
        if (
          this.$refs.fileExplorer &&
          typeof this.$refs.fileExplorer.updateTreeAfterOperation === 'function'
        ) {
          this.$refs.fileExplorer.updateTreeAfterOperation(
            itemToDelete,
            'delete'
          )
        } else {
          // Fallback se o método não estiver disponível
          this.refreshExplorer()
        }

        return Promise.resolve()
      } catch (error) {
        console.error('Erro ao excluir item:', error, this.selectedItem)
        //this.notifyError(this.$vs, `Erro ao excluir ${this.selectedItem.type === 'directory' ? 'a Pasta' : 'o Arquivo'}: ${error.message}`, 6000);
        return Promise.reject(error)
      } finally {
        this.deleting = false
      }
    },

    handleShowItemDetails(item) {
      this.selectedItem = item
      this.showItemDetails = true
    },

    /**
     * Manipular evento de renomeação de item (arquivo/pasta)
     * @param {Object} item Item que foi renomeado
     * @param {String} newName O novo nome do item
     */
    handleItemRenamed(item, newName) {
      if (!item || !newName) return

      console.log(`Item renomeado: ${item.name} → ${newName}`)

      try {
        // Criar uma cópia do item para uso na atualização
        const renamedItem = {
          ...item,
          oldName: item.name, // Guardar o nome antigo para referência
          name: newName,
          path: item.path.replace(item.name, newName)
        }

        // Usar a função genérica para atualizar a árvore e interface
        if (
          this.$refs.fileExplorer &&
          typeof this.$refs.fileExplorer.updateTreeAfterOperation === 'function'
        ) {
          this.$refs.fileExplorer.updateTreeAfterOperation(
            renamedItem,
            'rename'
          )
        } else {
          // Fallback se o método não estiver disponível
          this.refreshExplorer()
        }
      } catch (error) {
        console.error('Erro ao processar item renomeado:', error)
      }
    },

    /**
     * Manipula o download de um arquivo usando a url_tmp
     * @param {Object} file Objeto do arquivo a ser baixado
     */
    downloadFile(file) {
      if (!file || !file.url_tmp) {
        this.notifyError(
          this.$vs,
          'URL temporária não disponível para download.'
        )
        return
      }

      try {
        const normalizedName = this.ensureFileExtension(
          file.name || 'download',
          file.extension,
          file.mime_type
        )
        this.$utils.browser.forceDownload(
          file.url_tmp,
          normalizedName,
          file.mime_type || 'application/octet-stream'
        )

        this.notifySuccess(this.$vs, 'O download do arquivo foi iniciado...')
      } catch (error) {
        console.error('Erro ao iniciar download:', error)
        this.notifyError(
          this.$vs,
          'Não foi possível iniciar o download do arquivo'
        )
      }
    },
    ensureFileExtension(fileName, extension, mimeType) {
      if (!fileName) return 'download'
      const normalizedExtension = extension ? extension.toLowerCase() : ''
      if (normalizedExtension && fileName.toLowerCase().endsWith(`.${normalizedExtension}`)) {
        return fileName
      }

      if (normalizedExtension) {
        return `${fileName}.${normalizedExtension}`
      }

      const mimeMap = {
        'application/zip': 'zip',
        'application/x-zip-compressed': 'zip',
        'application/x-rar-compressed': 'rar',
        'application/vnd.rar': 'rar',
        'application/x-7z-compressed': '7z'
      }

      const mapped = mimeType ? mimeMap[mimeType] : null
      return mapped ? `${fileName}.${mapped}` : fileName
    },

    /**
     * Foca o input de nome da pasta quando o popup for aberto
     */
    focusNewFolderInput() {
      // Usar um setTimeout para garantir que o popup esteja completamente aberto
      setTimeout(() => {
        // Tentar diferentes abordagens para focar o input
        try {
          // 1. Tentar pela referência do Vue
          if (this.$refs.newFolderNameInput) {
            this.$refs.newFolderNameInput.focus()
            return
          }

          // 2. Tentar pelo ID do elemento
          const input = document.getElementById('folderName')
          if (input) {
            input.focus()
            return
          }

          // 3. Tentar usando seletor CSS mais específico
          const inputBySelector = document.querySelector(
            '.vs-popup--content #folderName'
          )
          if (inputBySelector) {
            inputBySelector.focus()
            return
          }

          console.warn('Não foi possível encontrar o input para focar')
        } catch (error) {
          console.error('Erro ao tentar focar input:', error)
        }
      }, 300) // Um atraso maior para garantir que o DOM foi completamente renderizado
    },

    // Função para abrir o modal de compartilhamento com um recurso
    openShareResourcePopup(resource) {
      this.shareResource = resource
      this.showShareResourcePopup = true
    },

    // Manipulador para evento de compartilhamento
    handleResourceShared(data) {
      console.log('Recurso compartilhado com sucesso:', data)

      // Notificar o usuário
      this.$vs.notify({
        title: 'Compartilhamento',
        text: `${
          data.resource.type === 'directory'
            ? 'Pasta'
            : data.resource.type === 'drive'
            ? 'Drive'
            : 'Arquivo'
        } "${data.resource.name}" compartilhado com ${
          data.users.length
        } usuários e ${data.groups.length} grupos.`,
        color: 'success',
        position: 'top-right'
      })
    },

    handleShowSharingInfo(item) {
      this.sharingDetailsItem = item
      this.showSharingDetails = true
    },

    // Adicionar este método na lista de métodos do explorador de arquivos
    handleShareRequest(item) {
      this.openShareResourcePopup(item)
    }
  },
  watch: {
    showNewFolderDialog(newValue) {
      if (newValue) {
        // O diálogo foi aberto, focar o campo após um pequeno atraso
        setTimeout(() => {
          if (this.$refs.newFolderNameInput) {
            this.$refs.newFolderNameInput.focus()
          }
        }, 100)
      } else {
        // O diálogo foi fechado, limpar o nome da pasta
        this.newFolderName = ''
      }
    },
    showSharingDetails(newValue) {
      if (!newValue) {
        this.sharingDetailsItem = null
      }
    }
  }
}
</script>

<style lang="scss">
/* Estilos foram movidos para o arquivo @/assets/scss/FileExplorer.scss */
</style>
